import { Fragment } from "preact";
import { useContext, useEffect, useState } from "preact/hooks";
import { route } from "preact-router";
import { Text } from "preact-i18n";

import LegacyOrderSummaryItem from "../shared/LegacyOrderSummaryItem.jsx";
import { useMediaQuery } from "react-responsive";

import { Configs } from "../../helpers/context";
import { formatDate, formatISODate } from "../../helpers/formatters/date";
import { formatIntegerCurrencyString } from "../../helpers/formatters";
import {
  EMIT_LOAD_INVOICE,
  EMIT_LOAD_PDF_DOWNLOAD_BUTTON,
  PDF_DOWNLOAD_BUTTON_CLICK,
  EMIT_LOAD_ATTACHMENT,
  EMIT_LOAD_BUSINESS_INFO,
  PAYMENT_DISABLED_IMPRESSION,
} from "../../helpers/event-definitions";

import SurchargeConfirmation from "../SurchargeConfirmation";
import PoyntCollectForm from "../PoyntCollectForm/PoyntCollectForm";
import CentralOrderSummary from "../shared/CentralOrderSummary";
import OrderSummaryAmounts from "../shared/OrderSummaryAmounts";
import TipsForm from "../shared/TipsForm";
import NotesForm from "../shared/NotesForm";
import Attachment from "../shared/Attachment";
import BusinessDetails from "../shared/BusinessDetails.jsx";
import "../../style/InvoicePage.css";
import "../../style/PaymentRequest.css";
import { useTrackImpressionEvent } from "../../hooks/use-track-impression-event";

const InvoicePage = ({
  order,
  payLink,
  product,
  setTransaction,
  setShowError,
  setErrorMsg,
  c2Analytics,
}) => {
  const configsContext = useContext(Configs);
  const [showDetails, setShowDetails] = useState(false);
  const [paymentRequestIdToPay, setPaymentRequestIdToPay] = useState('');
  const isDesktopOrLaptop = useMediaQuery({ minWidth: 820 });
  const [surchargeData, setSurchargeData] = useState(null);
  const [showSurchargeConfirmation, setShowSurchargeConfirmation] = useState(false);
  const isPaymentEnabled = (value) => value !== '0' && value !== false;
  const enablePayments = isPaymentEnabled(payLink?.enablePayment) ? '1' : '0';
  const paymentRequests = payLink.invoice && payLink.invoice.paymentRequests ? payLink.invoice.paymentRequests : null;
  useTrackImpressionEvent({
    shouldTrack: enablePayments === '0',
    onTrack: () => {
      c2Analytics?.trackCustomEvent(PAYMENT_DISABLED_IMPRESSION);
    }
  });

  // if we already paid, we need to go straight to the payment page
  if (payLink?.invoice?.status === "PAID") {
    setTransaction(payLink?.order?.transactions?.[0]);
    route("/transaction-complete");
    return;
  }

  const nearestUnpaidPaymentRequest = (paymentRequests || []).reduce((closestUnpaid, currentPayment) => {
    if (currentPayment.status !== "NOT_PAID") {
      return closestUnpaid;
    }

    if (!closestUnpaid) {
      return currentPayment;
    }

    return new Date(currentPayment.dueAt) < new Date(closestUnpaid.dueAt)
      ? currentPayment
      : closestUnpaid;
  }, null);

  const isLastPaymentRequest =
    (paymentRequests || []).filter(paymentRequest => paymentRequest.status === "NOT_PAID").length === 1

  useEffect(() => {
    if (nearestUnpaidPaymentRequest) {
      setPaymentRequestIdToPay(nearestUnpaidPaymentRequest.paymentRequestId);
    }
  }, [nearestUnpaidPaymentRequest])

  // track custom event on first load
  useEffect(() => {
    if (c2Analytics?.trackCustomEvent) {
      c2Analytics.trackCustomEvent(EMIT_LOAD_INVOICE);

      if (payLink.isPDFExperimentOn) {
        c2Analytics.trackCustomEvent(EMIT_LOAD_PDF_DOWNLOAD_BUTTON);
      }

      if (payLink?.isInvoiceBusinessDetailsExperimentOn) {
        c2Analytics.trackCustomEvent(EMIT_LOAD_BUSINESS_INFO);
      }

      if (payLink?.metadata?.attachments && payLink?.metadata?.attachments.length > 0) {
        c2Analytics.trackCustomEvent(EMIT_LOAD_ATTACHMENT, {
          attachmentCount: payLink.metadata.attachments.length,
          isMultipleFiles: payLink.metadata.attachments.length > 1,
        });
      }
    }
  }, []);

  useEffect(() => {
    setShowDetails(isDesktopOrLaptop);
  }, [isDesktopOrLaptop]);

  const invoiceId = payLink.invoice?.customInvoiceId || payLink.invoice?.orderId?.slice(0, 8);

  const toggleDetails = () => {
    setShowDetails(!showDetails);
  };

  const baseUrl = window.location.origin;

  const queryParams = new URLSearchParams({
    orderId: payLink?.invoice?.orderId || "",
  });

  const downloadPDFLink = `${baseUrl}/pdf?${queryParams.toString()}`;

  const handlePDFDownloadClick = () => {
    if (c2Analytics?.trackCustomEvent) {
      c2Analytics.trackCustomEvent(PDF_DOWNLOAD_BUTTON_CLICK);
    }
  };

  const setPaymentAmount = (paymentRequestId) => {
    setPaymentRequestIdToPay(paymentRequestId);
  }

  const orderLineItems =
    order && order.order && order.order.value && Array.isArray(order.order.value.lineItems)
      ? order.order.value.lineItems
      : [];

  const sumItemField = (items, field, filterFn) => {
    return items.reduce((total, item) => {
      const values = Array.isArray(item[field]) ? item[field] : [];
      const sum = values
        .filter(filterFn || (() => true))
        .reduce((subTotal, entry) => {
          const amount = entry && entry.amount && typeof entry.amount.value === 'number'
            ? entry.amount.value
            : 0;
          return subTotal + amount;
        }, 0);
      return total + sum;
    }, 0);
  }

  const itemLevelFeeSum = sumItemField(orderLineItems, 'fees', fee => fee.appliedBeforeTax);
  const itemLevelDiscountsSum =
    sumItemField(orderLineItems, 'discounts', discounts => discounts.appliedBeforeTax);
  const itemLevelTaxesSum = sumItemField(orderLineItems, 'taxes');
  const calculatedSubTotal =
    order?.order?.value?.totals?.subTotal?.value + itemLevelFeeSum + itemLevelTaxesSum - itemLevelDiscountsSum;
  const showAttachments = payLink?.metadata?.attachments && payLink?.metadata?.attachments.length > 0;
  const showCustomFooter = payLink?.isInvoiceBusinessDetailsExperimentOn && payLink?.customFooter !== "";

  const header = (
    <Fragment>
      {/* For mobile view, show/hide details button */}
      <div className="mobile-view">
        <button onClick={toggleDetails} className="invoice-button">
          {showDetails ? "Hide Details" : "Show Details"}
          {showDetails ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              transform="matrix(1,0,0,-1,0,0)"
            >
              <path
                d="M18.5302 8.61682C18.391 8.48331 18.1969 8.40625 17.9999 8.40625C17.803 8.40625 17.6089 8.48331 17.4697 8.61682L11.9999 13.8586L6.53021 8.61682C6.3894 8.48286 6.19892 8.40783 6.00051 8.40817C5.80209 8.40852 5.6119 8.48421 5.4716 8.61867C5.3313 8.75313 5.25232 8.93539 5.25195 9.12554C5.25159 9.31569 5.32988 9.49823 5.46967 9.63317L11.4697 15.3832C11.6089 15.5167 11.803 15.5937 11.9999 15.5937C12.1969 15.5937 12.391 15.5167 12.5302 15.3832L18.5302 9.63317C18.6695 9.49973 18.7499 9.31376 18.7499 9.125C18.7499 8.93623 18.6695 8.75027 18.5302 8.61682Z"
                fill="#444444"
              ></path>
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M18.5302 8.61682C18.391 8.48331 18.1969 8.40625 17.9999 8.40625C17.803 8.40625 17.6089 8.48331 17.4697 8.61682L11.9999 13.8586L6.53021 8.61682C6.3894 8.48286 6.19892 8.40783 6.00051 8.40817C5.80209 8.40852 5.6119 8.48421 5.4716 8.61867C5.3313 8.75313 5.25232 8.93539 5.25195 9.12554C5.25159 9.31569 5.32988 9.49823 5.46967 9.63317L11.4697 15.3832C11.6089 15.5167 11.803 15.5937 11.9999 15.5937C12.1969 15.5937 12.391 15.5167 12.5302 15.3832L18.5302 9.63317C18.6695 9.49973 18.7499 9.31376 18.7499 9.125C18.7499 8.93623 18.6695 8.75027 18.5302 8.61682Z"
                fill="#444444"
              />
            </svg>
          )}
        </button>
        {payLink.isPDFExperimentOn ? (
          <a
            className="invoice-download"
            href={downloadPDFLink}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handlePDFDownloadClick}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="size-6"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"
              />
            </svg>
            <Text id="DOWNLOAD">Download</Text>
          </a>
        ) : null}
      </div>

      {/* For desktop view, or if showDetails is true in mobile view, show full details */}
      {(showDetails || isDesktopOrLaptop) && (
        <div>
          <div className="invoice-details">
            <div>
              <span className="invoice-heading-bill-to">
                <Text id="INVOICE_CUSTOMER_BILL_TO">Customer (Bill To)</Text>
              </span>
              <div className="invoice-details-contact">
                {payLink.invoice?.firstName} {payLink.invoice?.lastName}
              </div>
              {payLink.metadata?.customer?.organization && (
                <div className="invoice-details-contact">
                  {payLink.metadata?.customer?.organization}
                </div>
              )}
              <div className="invoice-details-contact">{payLink.invoice?.customerEmail}</div>
            </div>
            <div>
              <div className="invoice-details-heading-section">
                <span className="invoice-details-heading">
                  <Text id="INVOICE_INVOICE">Invoice</Text>
                </span>{" "}
                No. #{invoiceId}
              </div>
              <div className="invoice-details-sent-info">
                <span className="invoice-details-date">
                  <Text id="INVOICE_DATE">Invoice Date</Text>:{" "}
                </span>
                {formatDate(
                  payLink.invoice?.executedAt || payLink.invoice?.createdAt,
                  configsContext?.configs?.ecommerceLocaleLanguage,
                  { dateStyle: "long" },
                )}
              </div>
              {!paymentRequests && (
                <div
                  className={
                    "invoice-details-due-by" +
                    (new Date() > new Date(payLink.invoice?.dueAt) ? " invoice-details-overdue" : "")
                  }
                >
                  <span className="invoice-details-date">
                    <Text id="INVOICE_DUE_DATE">Due Date</Text>:{" "}
                  </span>
                  {formatDate(
                    payLink.invoice?.dueAt ||
                      payLink.invoice?.executedAt ||
                      payLink.invoice?.createdAt,
                    configsContext?.configs?.ecommerceLocaleLanguage,
                    { dateStyle: "long" },
                  )}
                </div>
              )}
            </div>
          </div>
          {payLink.title && (
            <>
              <div class="order-summary-divider"></div>
              <div class="invoice-title">{payLink.title}</div>
            </>
          )}
          {payLink.invoice?.message ? (
            <div class="invoice-message">{payLink.invoice.message}</div>
          ) : null}
          <div className="order-items">
            <div className="order-items-header">
              <span className="order-item">
                <Text id="ORDER_ITEM">Item</Text>
              </span>
              <span className="order-item order-text-center">
                <Text id="ORDER_ITEM_QTY">Qty</Text>
              </span>
              <span className="order-item price order-text-right">
                <Text id="UNIT_PRICE">Unit Price</Text>
              </span>
              <span className="order-item amount">
                <Text id="ORDER_ITEM_AMOUNT">Amount</Text>
              </span>
            </div>
          </div>
          {order?.isLegacy ? (
            <Fragment>
              <div>
                {order?.order?.value?.items?.map((item) => {
                  return <LegacyOrderSummaryItem item={item} />;
                })}
                <div class="order-summary-divider"></div>
              </div>
              <OrderSummaryAmounts
                discountTotal={order?.order?.value?.amounts?.discountTotal}
                feeTotal={order?.order?.value?.amounts?.feeTotal}
                taxTotal={order?.order?.value?.amounts?.taxTotal}
                subTotal={order?.order?.value?.amounts?.subTotal}
                tipAmount={order?.order?.value?.amounts?.tipAmount}
                total={order?.order?.value?.amounts?.transactionAmount}
                invoice={payLink?.invoice}
              />
            </Fragment>
          ) : (
            <CentralOrderSummary order={order} payLink={payLink} />
          )}
          {(showAttachments || showCustomFooter) && (
            <div className="order-summary-divider"></div>
          )}
          {showAttachments && (
            <Attachment payLink={payLink} c2Analytics={c2Analytics} />
          )}
          {showCustomFooter ? (
            <div className="invoice-business-footer">{payLink?.customFooter}</div>
          ) : null}
          {payLink?.invoice?.paymentRequests && (
            <>
              <div className="order-summary-divider"></div>
              <div className="order-total">
              <span style="float:left">
                <Text id="CHECKOUT_PAYMENT_SCHEDULE">Payment Schedule</Text>
              </span>
                <div style="clear:both;"></div>
              </div>
              {payLink.invoice.paymentRequests.map((item) => {
                return (
                  <div className="order-amounts">
                    <span style="float:left">
                      {item.title}
                      <span className="order-amounts-sub-text">
                        <Text id="CHECKOUT_DUE_ON">Due on</Text>{
                          item.dueAtLocalDate 
                            ? ` ${formatISODate(item.dueAtLocalDate)}`
                            : ` ${formatDate(
                              item.dueAt,
                              configsContext?.configs?.ecommerceLocaleLanguage,
                              {
                                year: "numeric",
                                month: "short",
                                day: "2-digit",
                              },
                            )}`
                        }
                      </span>
                    </span>
                    <span style="float:right;text-align:right">
                      {formatIntegerCurrencyString(
                        item.amount,
                        configsContext?.configs?.currency,
                        configsContext?.configs?.ecommerceLocaleLanguage,
                      )}
                    </span>
                    <div style="clear:both;"></div>
                  </div>
                )
              })}
            </>
          )}
        </div>
      )}
    </Fragment>
  );

  return (
    <div>
      {enablePayments === "0" && (
        <div className="error-toast payment-disabled-warning">
          <div className="error-icon">⚠</div>
          <div className="error-text">
            <strong>
              <Text id="PAYMENT_DISABLED_HEADER">We can't accept online payments right now</Text>
            </strong>
            <div>
              <Text id="PAYMENT_DISABLED_DESCRIPTION_PART1">
                Sorry about that—we're not able to process payments through this invoice link at the
                moment.
              </Text>
              <br />
              <Text id="PAYMENT_DISABLED_DESCRIPTION_PART2">
                Please reach out to us directly to complete your payment.
              </Text>
            </div>
          </div>
        </div>
      )}

      <div className={`invoice-page ${enablePayments === "0" ? "invoice-only" : ""}`}>
        {enablePayments !== "0" && (
          <div className="collect-form-container">
            <div className={`container ${showSurchargeConfirmation ? "hidden" : ""}`}>
              {!isLastPaymentRequest && nearestUnpaidPaymentRequest && (
                <div className="payment-requests-option">
                  <div className="payment-requests-option-title">
                    <Text id="INVOICE_SELECT_AMOUNT_TO_PAY">Select amount to pay</Text>
                  </div>
                  <label className="payment-requests-option-row">
                    <input
                      type="radio"
                      name="payment-requests-due"
                      checked={!!paymentRequestIdToPay}
                      onClick={() => setPaymentAmount(nearestUnpaidPaymentRequest.paymentRequestId)}
                    />
                    <div className="payment-requests-option-label">
                      <div className="payment-requests-option-label-title">
                        <Text id="INVOICE_SELECT_AMOUNT_DUE">Amount Due</Text>
                      </div>
                      <div className="payment-requests-option-label-amount">
                        {formatIntegerCurrencyString(
                          nearestUnpaidPaymentRequest.amount,
                          configsContext?.configs?.currency,
                          configsContext?.configs?.ecommerceLocaleLanguage,
                        )}
                      </div>
                    </div>
                  </label>
                  <label className="payment-requests-option-row">
                    <input
                      type="radio"
                      name="payment-requests-total"
                      checked={!paymentRequestIdToPay}
                      onClick={() => setPaymentAmount('')}
                    />
                    <div className="payment-requests-option-label">
                      <div className="payment-requests-option-label-title">
                        <Text id="INVOICE_SELECT_INVOICE_TOTAL">Invoice Total</Text>
                      </div>
                      <div className="payment-requests-option-label-amount">
                        {formatIntegerCurrencyString(
                          calculatedSubTotal,
                          configsContext?.configs?.currency,
                          configsContext?.configs?.ecommerceLocaleLanguage,
                        )}
                      </div>
                    </div>
                  </label>
                </div>
              )}
              {("allowTips" in payLink.metadata && payLink.metadata.allowTips === "0") ||
              !payLink?.enableGratuity ||
              payLink?.enableGratuity === "0" ? (
                <div className="collect-form-total">
                  <span style="float:left">
                    <Text id="CHECKOUT_TOTAL">Total</Text>
                  </span>
                  <span style="float:right">
                    {order?.isLegacy
                      ? formatIntegerCurrencyString(
                          order?.order?.value?.amounts?.netTotal,
                          configsContext?.configs?.currency,
                          configsContext?.configs?.ecommerceLocaleLanguage,
                        )
                      : formatIntegerCurrencyString(
                          order?.order?.value?.totals?.total?.value,
                          configsContext?.configs?.currency,
                          configsContext?.configs?.ecommerceLocaleLanguage,
                        )}
                  </span>
                </div>
              ) : (
                <div className="invoice-tips-form">
                  <TipsForm
                    payLink={payLink}
                    order={order}
                    paymentRequestIdToPay={paymentRequestIdToPay}
                  />
                </div>
              )}
              {payLink?.metadata?.notes?.collectNotes && (
                <NotesForm
                  notesSignal={order?.notes}
                  trackCustomEvent={c2Analytics?.trackCustomEvent}
                />
              )}
              <PoyntCollectForm
                payLink={payLink}
                order={order}
                product={product}
                setTransaction={setTransaction}
                setShowError={setShowError}
                setErrorMsg={setErrorMsg}
                setSurchargeData={setSurchargeData}
                setShowSurchargeConfirmation={setShowSurchargeConfirmation}
                trackCustomEvent={c2Analytics?.trackCustomEvent}
                paymentRequestIdToPay={paymentRequestIdToPay}
              />
            </div>
            {showSurchargeConfirmation ? (
              <SurchargeConfirmation
                setErrorMsg={setErrorMsg}
                setShowError={setShowError}
                surchargeData={surchargeData}
                setTransaction={setTransaction}
                setSurchargeData={setSurchargeData}
                setShowSurchargeConfirmation={setShowSurchargeConfirmation}
                trackCustomEvent={c2Analytics?.trackCustomEvent}
              />
            ) : null}
          </div>
        )}
        <div className="invoice-summary">
          <div className="container">
            <div className="invoice-heading">
              <h1>
                <Text
                  id="INVOICE_FROM"
                  fields={{ businessName: configsContext?.configs?.ecommerceBusinessName }}
                >
                  Invoice from {configsContext?.configs?.ecommerceBusinessName}
                </Text>
              </h1>
              {isDesktopOrLaptop && payLink.isPDFExperimentOn ? (
                <a
                  className="invoice-download"
                  href={downloadPDFLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handlePDFDownloadClick}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                    class="size-6"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"
                    />
                  </svg>
                  <Text id="DOWNLOAD">Download</Text>
                </a>
              ) : null}
            </div>
            {payLink?.showAddressOnReceipts === "1" && payLink?.businessAddress && (
              <div className="invoice-address">{payLink.businessAddress}</div>
            )}
            {payLink?.isInvoiceBusinessDetailsExperimentOn && (
              <BusinessDetails
                socialWebsite={payLink?.socialWebsite}
                email={payLink?.email}
                phone={payLink?.phone}
                customContent={payLink?.customContent}
                isDesktopOrLaptop={isDesktopOrLaptop}
              />
            )}
            <div className="container invoice-order-summary">{header}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoicePage;
